import { useMemo } from 'react';
import {
  canAccessFeature,
  hasRole,
  normalizeSubscriptionTier,
  TierFeatureKey,
} from '@court-sync/shared';
import {
  getGuardianNavItemsWithAcademy,
  getUnifiedConsumerNavItems,
  getUnifiedConsumerNavStructure,
  type SidebarNavItem,
  type UnifiedConsumerNavStructure,
} from '@/components/layout/sidebar-nav-config';
import {
  getUnifiedConsumerMobileTabItems,
  type MobileTabItem,
} from '@/components/layout/mobile-tab-nav-config';
import { useAuthStore } from '@/store/auth';
import { useOrgMemberStore } from '@/store/orgMember';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useAcademyFeatureAccess } from '@/hooks/useFeatureAccess';

export function useUnifiedConsumerNav(): {
  navItems: SidebarNavItem[];
  navStructure: UnifiedConsumerNavStructure | null;
  mobileTabItems: MobileTabItem[];
  hasLessonsAccess: boolean;
  hasClubMemberAccess: boolean;
  hasMemberships: boolean;
  hasCourtHire: boolean;
  hasInventoryHire: boolean;
  hasMatchPlay: boolean;
  useUnifiedNav: boolean;
} {
  const { user } = useAuthStore();
  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const orgSlug = useOrgMemberStore((s) => s.orgSlug);
  const orgMemberAcademy = useOrgMemberStore((s) => s.academy);
  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const academy = guardianAcademy ?? orgMemberAcademy ?? orgProfile?.academy ?? null;
  const { p } = useOrgPaths(orgProfile ? { slug: orgProfile.organization.slug } : null);

  const { hasAccess: hasPortalTierAccess } = useAcademyFeatureAccess(
    academy,
    TierFeatureKey.STUDENT_GUARDIAN_PORTAL
  );

  return useMemo(() => {
    const useUnifiedNav = Boolean(orgProfile && orgSlug && p);

    if (!user || !useUnifiedNav || !p) {
      return {
        navItems: [],
        navStructure: null,
        mobileTabItems: [],
        hasLessonsAccess: false,
        hasClubMemberAccess: false,
        hasMemberships: false,
        hasCourtHire: false,
        hasInventoryHire: false,
        hasMatchPlay: false,
        useUnifiedNav: false,
      };
    }

    const hasStudentOrGuardianRole = hasRole(user, 'guardian') || hasRole(user, 'student');
    const hasMemberRole = hasRole(user, 'member');
    const academyTierOk =
      !academy ||
      hasPortalTierAccess ||
      normalizeSubscriptionTier(academy.subscriptionTier) !== 'starter';

    const hasLessonsAccess =
      hasStudentOrGuardianRole &&
      orgProfile.modules.hasAcademy &&
      Boolean(academy ?? orgProfile.academySlug) &&
      academyTierOk;

    const hasMemberships =
      hasMemberRole && orgProfile.modules.membershipsEntitled;
    const hasCourtHire = hasMemberRole && orgProfile.modules.courtHireEntitled;
    const hasInventoryHire = hasMemberRole && orgProfile.modules.hasClub;
    const hasClubMemberAccess =
      hasMemberRole &&
      orgProfile.modules.hasClub &&
      (hasMemberships || hasCourtHire || hasInventoryHire);

    const academyTier = normalizeSubscriptionTier(academy?.subscriptionTier ?? null) ?? 'starter';
    const hasMatchPlay =
      (hasStudentOrGuardianRole || hasMemberRole) &&
      (orgProfile.modules.playEventsEntitled ||
        orgProfile.modules.hasClub ||
        canAccessFeature(academyTier, TierFeatureKey.MATCH_PLAY));

    const pathFor = (subpath: string) => p(subpath) ?? subpath;

    const navOptions = {
      hasLessonsAccess,
      hasClubMemberAccess,
      hasMemberships,
      hasCourtHire,
      hasInventoryHire,
      hasMatchPlay,
      pathFor,
    };

    const navStructure = getUnifiedConsumerNavStructure(navOptions);
    const navItems = getUnifiedConsumerNavItems(navOptions);
    const mobileTabItems = getUnifiedConsumerMobileTabItems(navOptions);

    return {
      navItems,
      navStructure,
      mobileTabItems,
      hasLessonsAccess,
      hasClubMemberAccess,
      hasMemberships,
      hasCourtHire,
      hasInventoryHire,
      hasMatchPlay,
      useUnifiedNav: true,
    };
  }, [user, orgProfile, orgSlug, p, academy, hasPortalTierAccess]);
}

/** Legacy academy-scoped guardian nav when not in unified org portal context. */
export function useLegacyGuardianNav(academySlug: string | undefined): {
  navItems: SidebarNavItem[];
  mobileTabItems: MobileTabItem[];
} {
  return useMemo(() => {
    if (!academySlug) {
      return { navItems: [], mobileTabItems: [] };
    }
    const navItems = getGuardianNavItemsWithAcademy(academySlug);
    return { navItems, mobileTabItems: [] };
  }, [academySlug]);
}
